import React, { useCallback, useEffect, useState } from 'react';
import api from '../../services/api.js';
import './charger-models-compatibility.scss';

const PAGE_SIZE = 10;
const FEATURES = ['Core', 'Smart Charging', 'Reservation', 'Remote Trigger'];
const MEASURANDS = ['Energy.Active.Import.Register', 'Power.Active.Import', 'Current.Import', 'Voltage', 'SoC', 'Temperature'];
const tierClass = (t) => (t === 'Certified' ? 'badge-certified' : t === 'Compatible' ? 'badge-compatible' : 'badge-unsupported');

function Modal({ id, title, icon, onClose, children, footer, size = 'lg' }) {
  return (
    <div className="modal d-block cmc-modal-backdrop" id={id} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby={`${id}-title`}>
      <div className={`modal-dialog modal-${size} modal-dialog-centered`}>
        <div className="modal-content shadow-lg">
          <div className="modal-header">
            <h5 className="modal-title fw-bold" id={`${id}-title`}>
              <i className={`fas ${icon} me-2`} aria-hidden="true" />{title}
            </h5>
            <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
          </div>
          <div className="modal-body p-4">{children}</div>
          {footer && <div className="modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
}

function ProfileForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({
    vendor: initial?.vendor || '',
    model: initial?.model || '',
    firmwarePattern: initial?.firmwarePattern || '^3\\.[0-9]+\\..*$',
    baselineFirmware: initial?.baselineFirmware || '',
    ocppVersion: initial?.ocppVersion || '1.6-J',
    tier: initial?.tier || 'Compatible',
    nameplateAmps: initial?.nameplateAmps ?? 32,
    featureProfiles: initial?.featureProfiles || ['Core', 'Smart Charging', 'Reservation'],
    measurands: initial?.measurands || ['Energy.Active.Import.Register'],
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const toggle = (k, v) => setF((s) => ({ ...s, [k]: s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v] }));

  const submit = async () => {
    const next = {};
    if (!f.vendor.trim()) next.vendor = 'Vendor is required.';
    if (!f.model.trim()) next.model = 'Model is required.';
    try {
      new RegExp(f.firmwarePattern);
      if (!f.firmwarePattern.trim()) next.firmwarePattern = 'Firmware pattern is required.';
    } catch {
      next.firmwarePattern = 'Firmware pattern must be a valid regular expression.';
    }
    if (!(Number(f.nameplateAmps) > 0)) next.nameplateAmps = 'Nameplate rating must be greater than 0.';
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    setServerError('');
    try {
      await onSave({
        vendor: f.vendor.trim(),
        model: f.model.trim(),
        firmwarePattern: f.firmwarePattern.trim(),
        baselineFirmware: f.baselineFirmware.trim() || null,
        ocppVersion: f.ocppVersion,
        tier: initial ? undefined : f.tier,
        nameplateAmps: Number(f.nameplateAmps),
        featureProfiles: f.featureProfiles,
        measurands: f.measurands,
      });
    } catch (err) {
      setServerError(err.message || 'Could not save the profile.');
      setSaving(false);
    }
  };

  const inv = (k) => (errors[k] ? 'is-invalid' : '');
  return (
    <Modal
      id="modal-add-profile"
      title={initial ? 'Edit Charger Model Profile' : 'Register Charger Model Profile'}
      icon="fa-plus"
      onClose={onCancel}
      footer={(
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onCancel}>Cancel</button>
          <button type="button" className="btn btn-primary" id="btn-save-profile" onClick={submit} disabled={saving}>
            {saving ? 'Saving…' : 'Save Profile'}
          </button>
        </>
      )}
    >
      {serverError && <div className="alert alert-danger py-2" id="profile-error" role="alert">{serverError}</div>}
      <form id="form-add-profile" onSubmit={(e) => e.preventDefault()} noValidate>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label small fw-bold" htmlFor="p-vendor">Vendor Name</label>
            <input type="text" className={`form-control ${inv('vendor')}`} id="p-vendor" value={f.vendor} onChange={set('vendor')} placeholder="Manufacturer name" />
            <div className="invalid-feedback">{errors.vendor}</div>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-bold" htmlFor="p-model">Model Name / Family</label>
            <input type="text" className={`form-control ${inv('model')}`} id="p-model" value={f.model} onChange={set('model')} placeholder="e.g. Terra 184" />
            <div className="invalid-feedback">{errors.model}</div>
          </div>
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label small fw-bold" htmlFor="p-fw">Firmware Version Pattern (Regex)</label>
            <input type="text" className={`form-control ${inv('firmwarePattern')}`} id="p-fw" value={f.firmwarePattern} onChange={set('firmwarePattern')} />
            <div className="invalid-feedback">{errors.firmwarePattern}</div>
          </div>
          <div className="col-md-6">
            <label className="form-label small fw-bold" htmlFor="p-proto">OCPP Protocol Version</label>
            <select className="form-select" id="p-proto" value={f.ocppVersion} onChange={set('ocppVersion')}>
              <option value="1.6-J">OCPP 1.6-J (JSON / WebSocket)</option>
              <option value="2.0.1">OCPP 2.0.1 (JSON / WebSocket)</option>
            </select>
          </div>
        </div>
        <div className="row g-3 mb-3">
          <div className="col-md-6">
            <label className="form-label small fw-bold" htmlFor="p-tier">Initial Compatibility Tier</label>
            <select className="form-select" id="p-tier" value={f.tier} onChange={set('tier')} disabled={!!initial}>
              <option value="Compatible">Compatible (Safe Fallbacks)</option>
              <option value="Certified">Certified (Commissioned)</option>
              <option value="Unsupported">Unsupported</option>
            </select>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold" htmlFor="p-amps">Nameplate Rating (A)</label>
            <input type="number" min="0" step="0.1" className={`form-control ${inv('nameplateAmps')}`} id="p-amps" value={f.nameplateAmps} onChange={set('nameplateAmps')} />
            <div className="invalid-feedback">{errors.nameplateAmps}</div>
          </div>
          <div className="col-md-3">
            <label className="form-label small fw-bold" htmlFor="p-baseline">Baseline Firmware</label>
            <input type="text" className="form-control" id="p-baseline" value={f.baselineFirmware} onChange={set('baselineFirmware')} placeholder="e.g. 3.4.0" />
          </div>
        </div>
        <div className="mb-3">
          <span className="form-label small fw-bold d-block">Hardware Feature Flags (OCPP feature profiles)</span>
          {FEATURES.map((ft) => (
            <div className="form-check form-check-inline" key={ft}>
              <input type="checkbox" className="form-check-input" id={`p-feat-${ft.replace(/\s/g, '-').toLowerCase()}`} checked={f.featureProfiles.includes(ft)} disabled={ft === 'Core'} onChange={() => toggle('featureProfiles', ft)} />
              <label className="form-check-label small fw-bold" htmlFor={`p-feat-${ft.replace(/\s/g, '-').toLowerCase()}`}>{ft === 'Reservation' ? 'Connector Reservation' : ft}</label>
            </div>
          ))}
        </div>
        <div>
          <span className="form-label small fw-bold d-block">Supported Measurands</span>
          {MEASURANDS.map((m) => (
            <div className="form-check form-check-inline" key={m}>
              <input type="checkbox" className="form-check-input" id={`p-mea-${m}`} checked={f.measurands.includes(m)} onChange={() => toggle('measurands', m)} />
              <label className="form-check-label small" htmlFor={`p-mea-${m}`}>{m}</label>
            </div>
          ))}
        </div>
      </form>
    </Modal>
  );
}

function SuiteRunner({ profiles, target, onClose, onChanged, flash }) {
  const [profile, setProfile] = useState(target || profiles[0] || null);
  const [run, setRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});

  const load = useCallback(async (p) => {
    setError('');
    setRun(null);
    if (!p) return;
    try {
      const runs = await api.listCommissioningRuns(p.id);
      const last = runs.find((r) => r.status === 'passed' || r.status === 'failed');
      if (last) setRun(await api.getCommissioningRun(last.id));
    } catch (e) {
      setError(e.message);
    }
  }, []);
  useEffect(() => { load(profile); }, [profile, load]);

  const start = async () => {
    setRunning(true);
    setError('');
    try {
      setRun(await api.startCommissioningRun(profile.id));
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  const promote = async () => {
    setError('');
    try {
      const p = await api.promoteChargerModel(profile.id);
      setProfile(p);
      flash(`${p.vendor} ${p.model} promoted to Certified.`);
      onChanged();
    } catch (e) {
      setError(e.message);
    }
  };

  const download = async () => {
    try {
      const blob = await api.downloadCommissioningReport(run.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `commissioning-${run.id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    }
  };

  const steps = run?.steps || [];
  return (
    <Modal
      id="modal-commissioning-suite"
      size="xl"
      title="Hardware Commissioning Test Suite Runner (CT-01 to CT-16)"
      icon="fa-vial"
      onClose={onClose}
      footer={(
        <>
          {run && <button type="button" className="btn btn-outline-primary me-auto" id="btn-download-report" onClick={download}><i className="fas fa-file-pdf me-1" />Signed Audit PDF</button>}
          {run && profile?.tier !== 'Certified' && (
            <button type="button" className="btn btn-primary" id="btn-promote-certified" onClick={promote} disabled={run.status !== 'passed'}>
              <i className="fas fa-award me-1" />Promote to Certified
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={onClose}>Close</button>
        </>
      )}
    >
      <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <div>
          <span className="text-muted small">Target Profile:</span>
          <select className="form-select form-select-sm d-inline-block w-auto ms-2" id="commissioning-target" value={profile?.id || ''} onChange={(e) => setProfile(profiles.find((p) => p.id === e.target.value))}>
            {profiles.map((p) => <option key={p.id} value={p.id}>{p.vendor} {p.model} ({p.ocppVersion})</option>)}
          </select>
          <strong className="ms-2 d-none" id="commissioning-target-name">{profile ? `${profile.vendor} ${profile.model}` : ''}</strong>
          {profile && <span className={`badge ${tierClass(profile.tier)} ms-2`}>{profile.tier}</span>}
        </div>
        <button className="btn btn-sm btn-success" id="btn-start-tests" onClick={start} disabled={running || !profile}>
          {running ? <><i className="fas fa-spinner fa-spin me-1" />Executing CT-01 to CT-16...</> : <><i className="fas fa-play me-1" />Execute All 16 Tests</>}
        </button>
      </div>
      {error && <div className="alert alert-danger py-2" id="suite-error" role="alert">{error}</div>}
      {run && (
        <div className={`alert py-2 ${run.status === 'passed' ? 'alert-success' : 'alert-warning'}`} id="suite-summary" role="status">
          Firmware {run.firmwareVersion}: {run.passedCount}/{run.totalCount} tests passed.
          {run.status === 'passed' ? ' Ready for promotion to Certified.' : ' Certification is blocked until all 16 pass.'}
        </div>
      )}
      <div className="table-responsive border rounded cmc-suite-scroll">
        <table className="table table-sm mb-0">
          <thead>
            <tr className="text-muted small text-uppercase">
              <th>Test ID</th><th>Test Specification</th><th>Profile Area</th><th>Expected OCPP Reply / Telemetry</th><th>Latency</th><th>Status</th>
            </tr>
          </thead>
          <tbody id="test-suite-rows">
            {steps.length === 0 && <tr><td colSpan={6} className="text-center text-muted py-4">No commissioning run recorded yet. Execute the suite to begin.</td></tr>}
            {steps.map((t) => (
              <React.Fragment key={t.testId}>
                <tr>
                  <td className="fw-bold text-dark font-monospace">{t.testId}</td>
                  <td className="fw-bold text-dark">
                    <button type="button" className="btn btn-link btn-sm p-0 me-1" aria-label={`Toggle payloads for ${t.testId}`} onClick={() => setOpen((o) => ({ ...o, [t.testId]: !o[t.testId] }))}>
                      <i className={`fas ${open[t.testId] ? 'fa-chevron-down' : 'fa-chevron-right'}`} />
                    </button>
                    {t.name}
                  </td>
                  <td><span className="badge badge-secondary">{t.area}</span></td>
                  <td className="small text-muted">{t.expected}</td>
                  <td className="small">{t.latencyMs} ms</td>
                  <td>
                    <span className={`badge px-2 ${t.status === 'Passed' ? 'badge-available' : 'badge-faulted'}`}>
                      <i className={`fas ${t.status === 'Passed' ? 'fa-check-circle' : 'fa-times-circle'} me-1`} />{t.status}
                    </span>
                  </td>
                </tr>
                {open[t.testId] && (
                  <tr>
                    <td colSpan={6} className="bg-light">
                      <div className="d-flex gap-3 flex-wrap">
                        <div><div className="small fw-bold">Request</div><pre className="cmc-payload mb-0">{JSON.stringify(t.requestPayload, null, 1)}</pre></div>
                        <div><div className="small fw-bold">Response</div><pre className="cmc-payload mb-0">{JSON.stringify(t.responsePayload, null, 1)}</pre></div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

export default function ChargerModelsCompatibilityPage() {
  const [data, setData] = useState({ items: [], total: 0 });
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [tier, setTier] = useState('');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | profile
  const [suite, setSuite] = useState(null); // null | { target }
  const [allProfiles, setAllProfiles] = useState([]);

  const flash = (m) => { setNotice(m); setTimeout(() => setNotice(''), 6000); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({ page, pageSize: PAGE_SIZE });
      if (tier) params.set('tier', tier);
      if (q.trim()) params.set('q', q.trim());
      const [res, sum] = await Promise.all([api.listChargerModels(params.toString()), api.getChargerModelSummary()]);
      setData({ items: res.data, total: res.meta.total });
      setSummary(sum);
    } catch (e) {
      setError(e.message || 'Could not load charger models.');
    } finally {
      setLoading(false);
    }
  }, [page, tier, q]);
  useEffect(() => { load(); }, [load]);

  const openSuite = async (target) => {
    try {
      const res = await api.listChargerModels('page=1&pageSize=50');
      setAllProfiles(res.data);
      setSuite({ target: target ? res.data.find((p) => p.id === target.id) : null });
    } catch (e) {
      setError(e.message);
    }
  };

  const save = async (body) => {
    if (editing && editing !== 'new') {
      await api.updateChargerModel(editing.id, body);
      flash('Model profile updated.');
    } else {
      await api.createChargerModel(body);
      flash('Model profile registered.');
    }
    setEditing(null);
    setPage(1);
    load();
  };

  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE));
  const tierCard = (name, color, icon, title, badge, text, cls) => (
    <div className="col-12 col-md-4 mb-3">
      <div className="card border shadow-sm h-100" style={{ borderLeft: `4px solid ${color}` }}>
        <div className="card-body p-3">
          <div className="d-flex justify-content-between align-items-center mb-1">
            <h6 className={`fw-bold mb-0 ${cls}`}><i className={`fas ${icon} me-1`} /> {title}</h6>
            <span className={`badge ${badge.cls}`}>{badge.text}</span>
          </div>
          <p className="small text-muted mb-2">{text}</p>
          <div className="small fw-bold text-dark" data-testid={`tier-count-${name}`}>
            {summary ? (name === 'Unsupported' ? `${summary.tiers[name].units} Connected Units` : `${summary.tiers[name].models} Active Models • ${summary.tiers[name].units} Deployed Units`) : '…'}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="container-fluid py-4" id="charger-models-container">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1" style={{ color: '#005293' }}>
            <i className="fas fa-microchip me-2" aria-hidden="true" />Charger Models &amp; Compatibility
          </h4>
          <p className="text-muted small mb-0">Vendor-neutral EVSE hardware abstraction layer, tier certifications, and commissioning test suites.</p>
        </div>
        <div className="mt-2 mt-md-0">
          <button className="btn btn-sm btn-outline-primary me-2" id="btn-run-commissioning" onClick={() => openSuite(null)}>
            <i className="fas fa-vial me-1" /> Run Commissioning Suite (CT-01..16)
          </button>
          <button className="btn btn-sm btn-primary" id="btn-register-profile" onClick={() => setEditing('new')}>
            <i className="fas fa-plus me-1" /> Register Model Profile
          </button>
        </div>
      </div>

      {notice && <div className="alert alert-success py-2" id="cmc-notice" role="status">{notice}</div>}
      {error && <div className="alert alert-danger py-2" id="cmc-error" role="alert">{error}</div>}

      <div className="row mb-4" id="tier-cards">
        {tierCard('Certified', '#16a34a', 'fa-check-double', 'Certified Tier', { cls: 'badge-success-soft', text: 'Full Feature Set' }, 'Passed all 16 commissioning tests (CT-01 to CT-16). Supports full dynamic load balancing, high-frequency telemetry, and reservation locking.', 'text-success')}
        {tierCard('Compatible', '#d97706', 'fa-shield-alt', 'Compatible Tier', { cls: 'badge-warning-soft', text: 'Safe Fallbacks Active' }, 'Speaks standard OCPP with minor quirks. Operates with 10% Safe Headroom power buffers, conservative ramp limits, and verified MeterValues monitoring.', '')}
        {tierCard('Unsupported', '#dc2626', 'fa-ban', 'Unsupported Tier', { cls: 'badge-danger-soft', text: 'Gateway Required' }, 'Proprietary or non-compliant hardware unable to establish native OCPP WebSocket connections. Requires external protocol translation gateway.', 'text-danger')}
      </div>

      <div className="card border shadow-sm mb-4">
        <div className="card-header bg-white d-flex justify-content-between align-items-center py-3 flex-wrap gap-2">
          <h6 className="mb-0 fw-bold" style={{ color: '#005293' }}>
            <i className="fas fa-list-ul me-2" aria-hidden="true" />Hardware Model Profiles &amp; Capabilities Matrix
          </h6>
          <div className="d-flex gap-2 align-items-center">
            <input className="form-control form-control-sm" style={{ width: 200 }} id="search-profile" placeholder="Search vendor or model" aria-label="Search profiles" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} />
            <select className="form-select form-select-sm" style={{ width: 150 }} id="filter-tier" aria-label="Filter by tier" value={tier} onChange={(e) => { setTier(e.target.value); setPage(1); }}>
              <option value="">All tiers</option>
              <option>Certified</option><option>Compatible</option><option>Unsupported</option>
            </select>
            <span className="text-muted small d-none d-lg-inline">Matched to chargers by vendor and model</span>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead>
                <tr className="text-muted small text-uppercase">
                  <th>Vendor &amp; Model</th><th>Firmware Pattern</th><th>Protocol</th><th>Tier</th><th>Smart Charging</th><th>Reservation</th><th>Status</th><th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody id="profiles-table-body">
                {loading && <tr><td colSpan={8} className="text-center py-4 text-muted">Loading…</td></tr>}
                {!loading && data.items.length === 0 && <tr><td colSpan={8} className="text-center py-4 text-muted">No model profiles match.</td></tr>}
                {!loading && data.items.map((p) => (
                  <tr key={p.id} data-profile-id={p.id}>
                    <td><strong className="text-dark">{p.vendor}</strong><span className="text-muted ms-1">{p.model}</span></td>
                    <td><code className="text-primary fw-bold">{p.firmwarePattern}</code></td>
                    <td><span className="badge badge-secondary">OCPP {p.ocppVersion}</span></td>
                    <td>
                      <span className={`badge ${tierClass(p.tier)} px-2 py-1`}><i className={`fas ${p.tier === 'Certified' ? 'fa-check' : 'fa-shield-alt'} me-1`} />{p.tier}</span>
                      {p.headroomActive && <div className="small text-warning-emphasis mt-1 headroom-flag">{p.headroomLabel} · max {p.effectiveMaxAmps}A of {p.nameplateAmps}A</div>}
                    </td>
                    <td>{p.smartCharging ? <i className="fas fa-check text-success" aria-label="Supported" /> : <i className="fas fa-times text-muted" aria-label="Not supported" />}</td>
                    <td>{p.reservation ? <i className="fas fa-check text-success" aria-label="Supported" /> : <i className="fas fa-times text-muted" aria-label="Not supported" />}</td>
                    <td>{p.isActive ? <span className="badge badge-available">Active</span> : <span className="badge badge-secondary">Disabled</span>}</td>
                    <td className="text-end text-nowrap">
                      <button className="btn btn-sm btn-outline-info btn-test-profile me-1" onClick={() => openSuite(p)}><i className="fas fa-vial me-1" />Test</button>
                      <button className="btn btn-sm btn-outline-secondary btn-edit-profile" aria-label={`Edit ${p.vendor} ${p.model}`} onClick={() => setEditing(p)}><i className="fas fa-edit" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card-footer d-flex justify-content-between align-items-center">
          <span className="small text-muted" id="profiles-count">{data.total} profile(s)</span>
          <div className="btn-group btn-group-sm">
            <button className="btn btn-outline-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page"><i className="fas fa-chevron-left" /></button>
            <span className="btn btn-outline-secondary disabled">{page} / {pages}</span>
            <button className="btn btn-outline-secondary" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page"><i className="fas fa-chevron-right" /></button>
          </div>
        </div>
      </div>

      {editing && <ProfileForm initial={editing === 'new' ? null : editing} onSave={save} onCancel={() => setEditing(null)} />}
      {suite && <SuiteRunner profiles={allProfiles} target={suite.target} onClose={() => { setSuite(null); load(); }} onChanged={load} flash={flash} />}
    </div>
  );
}
